import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useOrgProject } from '../contexts/OrgProjectContext';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  blueLt:     '#DBEAFE',
  green:      '#10B981',
  greenLt:    '#D1FAE5',
  white:      '#FFFFFF',
  slate:      '#64748B',
  slateLight: '#94A3B8',
  border:     '#E2E8F0',
  bg:         '#F8FAFC',
  text:       '#1E293B',
  textMid:    '#475569',
  red:        '#EF4444',
  redLt:      '#FEE2E2',
  yellow:     '#F59E0B',
  yellowLt:   '#FEF3C7',
};

// ─── Step Labels ──────────────────────────────────────────────────────────────
const STEPS = [
  { key: 'provider', label: 'Select Provider' },
  { key: 'credentials', label: 'Enter Credentials' },
  { key: 'scm_org', label: 'Select Organisation' },
  { key: 'confirm', label: 'Confirm' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function SCMWizard({ open, onClose, onCreated }) {
  const { orgId } = useOrgProject();
  const dialogRef = useRef(null);

  // Step management
  const [step, setStep] = useState(0);

  // Step 1: Provider
  const [provider, setProvider] = useState('');

  // Step 2: Credentials
  const [authType, setAuthType] = useState('pat');
  const [pat, setPat] = useState('');
  const [connectionName, setConnectionName] = useState('');

  // Step 3: SCM org/group
  const [scmOrgName, setScmOrgName] = useState('');
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgsError, setOrgsError] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  // Step 4: Confirm
  const [createSuccess, setCreateSuccess] = useState(false);

  // Shared state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingPat, setValidatingPat] = useState(false);
  const [patValidated, setPatValidated] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep(0);
      setProvider('');
      setAuthType('pat');
      setPat('');
      setConnectionName('');
      setScmOrgName('');
      setAvailableOrgs([]);
      setLoadingOrgs(false);
      setOrgsError('');
      setShowManualInput(false);
      setError('');
      setLoading(false);
      setValidatingPat(false);
      setPatValidated(false);
      setCreateSuccess(false);
    }
  }, [open]);

  // Trap focus inside dialog when open
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goBack = () => {
    setError('');
    setStep((s) => Math.max(0, s - 1));
  };

  const goNext = () => {
    setError('');
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  // ── Step 1: Provider selection ──────────────────────────────────────────────
  const handleProviderNext = () => {
    if (!provider) {
      setError('Please select a provider');
      return;
    }
    goNext();
  };

  // ── Step 2: Credentials ─────────────────────────────────────────────────────
  const handleValidatePat = useCallback(async () => {
    if (!pat.trim()) {
      setError('Please enter a Personal Access Token');
      return;
    }
    if (!connectionName.trim()) {
      setError('Please enter a connection name');
      return;
    }
    setError('');
    setValidatingPat(true);

    try {
      // Call the dedicated validation endpoint to verify the PAT
      const res = await apiFetch(`/api/orgs/${orgId}/scm-connections/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider,
          auth_type: 'pat',
          credentials: { token: pat.trim() },
        }),
      });

      if (res.ok) {
        // Token is valid — proceed to next step
        setPatValidated(true);
        goNext();
        return;
      }

      const body = await res.json().catch(() => ({}));

      if (res.status === 400 && body.code === 'CREDENTIALS_INVALID') {
        setError(body.error || 'Invalid credentials. Please check your token and try again.');
        return;
      }

      if (res.status === 502) {
        setError(body.error || 'Unable to reach the SCM provider. Please try again later.');
        return;
      }

      // Other errors (validation errors, etc.)
      setError(body.error || 'Failed to validate credentials. Please try again.');
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setValidatingPat(false);
    }
  }, [pat, connectionName, provider, orgId, goNext]);

  const handleCredentialsNext = () => {
    if (authType === 'pat') {
      handleValidatePat();
    } else {
      // OAuth flow — stub: show message
      setError('');
      goNext();
    }
  };

  // ── Step 3: SCM org selection ───────────────────────────────────────────────

  // Fetch available orgs/groups when entering step 3
  useEffect(() => {
    if (step !== 2) return;
    if (availableOrgs.length > 0) return; // Already fetched

    const token = pat.trim();
    if (!token || !provider) {
      setShowManualInput(true);
      return;
    }

    setLoadingOrgs(true);
    setOrgsError('');

    apiFetch(`/api/orgs/${orgId}/scm-connections/discover-orgs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider,
        credentials: { token },
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          const orgs = data.orgs || [];
          setAvailableOrgs(orgs);
          if (orgs.length === 0) {
            setShowManualInput(true);
            setOrgsError('No organisations found. Enter the name manually below.');
          }
        } else {
          // API error — fall back to manual input
          setShowManualInput(true);
          setOrgsError('Could not load organisations from provider. Enter the name manually below.');
        }
      })
      .catch(() => {
        setShowManualInput(true);
        setOrgsError('Could not load organisations from provider. Enter the name manually below.');
      })
      .finally(() => {
        setLoadingOrgs(false);
      });
  }, [step, provider, pat, orgId, availableOrgs.length]);

  const handleScmOrgNext = () => {
    if (!scmOrgName.trim()) {
      setError('Please enter the SCM organisation or group name');
      return;
    }
    setError('');
    goNext();
  };

  // ── Step 4: Confirm and create ──────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    setError('');
    setLoading(true);

    try {
      const body = {
        provider,
        connection_name: connectionName.trim(),
        auth_type: authType,
        credentials: authType === 'pat' ? { token: pat.trim() } : {},
        scm_org_name: scmOrgName.trim(),
      };

      const res = await apiFetch(`/api/orgs/${orgId}/scm-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (res.status === 400 && errBody.code === 'CREDENTIALS_INVALID') {
          setError(errBody.error || 'Failed to create connection. Please check your credentials.');
          setStep(1);
          return;
        }
        if (res.status === 401) {
          setError(errBody.error || 'Authentication failed. Please re-enter your credentials.');
          setStep(1);
          return;
        }
        if (res.status === 502) {
          setError(errBody.error || 'Unable to reach the SCM provider. Please try again later.');
          return;
        }
        setError(errBody.error || `Server error (${res.status}). Please try again.`);
        return;
      }

      const created = await res.json();
      setCreateSuccess(true);

      // Show success briefly, then close
      setTimeout(() => {
        if (onCreated) onCreated(created);
        onClose();
      }, 1500);
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [provider, connectionName, authType, pat, scmOrgName, orgId, onCreated, onClose]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderStepIndicator = () => (
    <div style={styles.stepIndicator}>
      {STEPS.map((s, i) => (
        <div key={s.key} style={styles.stepItem}>
          <div style={{
            ...styles.stepCircle,
            background: i < step ? C.green : i === step ? C.blue : C.border,
            color: i <= step ? C.white : C.slate,
          }}>
            {i < step ? '✓' : i + 1}
          </div>
          <span style={{
            ...styles.stepLabel,
            color: i === step ? C.text : C.slate,
            fontWeight: i === step ? 600 : 400,
          }}>
            {s.label}
          </span>
          {i < STEPS.length - 1 && <div style={styles.stepLine} />}
        </div>
      ))}
    </div>
  );

  const renderProviderStep = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>Select SCM Provider</h3>
      <p style={styles.stepDescription}>
        Choose the source control provider you want to connect.
      </p>
      <div style={styles.providerCards}>
        <button
          type="button"
          onClick={() => { setProvider('github'); setError(''); }}
          style={{
            ...styles.providerCard,
            borderColor: provider === 'github' ? C.blue : C.border,
            background: provider === 'github' ? C.blueLt : C.white,
          }}
          aria-pressed={provider === 'github'}
        >
          <span style={styles.providerIcon}>⚙</span>
          <span style={styles.providerName}>GitHub</span>
          <span style={styles.providerDesc}>Connect a GitHub organisation</span>
        </button>
        <button
          type="button"
          onClick={() => { setProvider('gitlab'); setError(''); }}
          style={{
            ...styles.providerCard,
            borderColor: provider === 'gitlab' ? C.blue : C.border,
            background: provider === 'gitlab' ? C.blueLt : C.white,
          }}
          aria-pressed={provider === 'gitlab'}
        >
          <span style={styles.providerIcon}>⚙</span>
          <span style={styles.providerName}>GitLab</span>
          <span style={styles.providerDesc}>Connect a GitLab group</span>
        </button>
      </div>
    </div>
  );

  const renderCredentialsStep = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>Enter Credentials</h3>
      <p style={styles.stepDescription}>
        Authenticate with {provider === 'github' ? 'GitHub' : 'GitLab'} using a Personal Access Token or OAuth.
      </p>

      {/* Auth type selector */}
      <div style={styles.authTypeRow}>
        <button
          type="button"
          onClick={() => { setAuthType('pat'); setError(''); }}
          style={{
            ...styles.authTypeBtn,
            background: authType === 'pat' ? C.blue : C.white,
            color: authType === 'pat' ? C.white : C.text,
            borderColor: authType === 'pat' ? C.blue : C.border,
          }}
          aria-pressed={authType === 'pat'}
        >
          Personal Access Token
        </button>
        <button
          type="button"
          onClick={() => { setAuthType('oauth'); setError(''); }}
          style={{
            ...styles.authTypeBtn,
            background: authType === 'oauth' ? C.blue : C.white,
            color: authType === 'oauth' ? C.white : C.text,
            borderColor: authType === 'oauth' ? C.blue : C.border,
          }}
          aria-pressed={authType === 'oauth'}
        >
          OAuth
        </button>
      </div>

      {authType === 'pat' ? (
        <>
          {/* Connection name */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="scm-connection-name">
              Connection Name <span style={styles.required}>*</span>
            </label>
            <input
              id="scm-connection-name"
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="e.g. My GitHub Org"
              maxLength={128}
              style={styles.input}
              aria-required="true"
            />
          </div>

          {/* PAT input */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="scm-pat-input">
              Personal Access Token <span style={styles.required}>*</span>
            </label>
            <input
              id="scm-pat-input"
              type="password"
              value={pat}
              onChange={(e) => { setPat(e.target.value); setPatValidated(false); }}
              placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
              maxLength={255}
              style={styles.input}
              aria-required="true"
              aria-describedby="scm-pat-hint"
              autoComplete="off"
            />
            <span id="scm-pat-hint" style={styles.hintText}>
              {pat.length}/255 characters. Token will be stored securely.
            </span>
          </div>
        </>
      ) : (
        <div style={styles.oauthPlaceholder}>
          {/* Connection name for OAuth */}
          <div style={{ ...styles.fieldGroup, textAlign: 'left', marginBottom: 16 }}>
            <label style={styles.label} htmlFor="scm-oauth-connection-name">
              Connection Name <span style={styles.required}>*</span>
            </label>
            <input
              id="scm-oauth-connection-name"
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="e.g. My GitHub Org"
              maxLength={128}
              style={styles.input}
              aria-required="true"
            />
          </div>

          <p style={styles.oauthText}>
            You will be redirected to {provider === 'github' ? 'GitHub' : 'GitLab'} to authorize access to your repositories.
          </p>
          <button
            type="button"
            style={styles.oauthBtn}
            onClick={() => {
              if (!connectionName.trim()) {
                setError('Please enter a connection name');
                return;
              }
              setError('');
              // Generate a CSRF state token for security
              const csrfToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
              // Store the CSRF token in sessionStorage for validation on callback
              sessionStorage.setItem('oauth_csrf_token', csrfToken);
              // Construct the OAuth authorization URL for the selected provider
              const oauthBaseUrl = provider === 'github'
                ? 'https://github.com/login/oauth/authorize'
                : 'https://gitlab.com/oauth/authorize';
              const redirectUri = `${window.location.origin}/oauth/callback/${provider}`;
              const statePayload = JSON.stringify({
                orgId,
                connectionName: connectionName.trim(),
                provider,
                csrf: csrfToken,
              });
              const params = new URLSearchParams({
                client_id: provider === 'github'
                  ? (process.env.REACT_APP_GITHUB_CLIENT_ID || '')
                  : (process.env.REACT_APP_GITLAB_CLIENT_ID || ''),
                redirect_uri: redirectUri,
                scope: provider === 'github' ? 'repo read:org' : 'api read_repository',
                state: statePayload,
                response_type: 'code',
              });
              window.location.href = `${oauthBaseUrl}?${params.toString()}`;
            }}
            aria-label={`Authorize with ${provider === 'github' ? 'GitHub' : 'GitLab'}`}
          >
            Authorize with {provider === 'github' ? 'GitHub' : 'GitLab'}
          </button>
          <span style={styles.hintText}>
            You will be redirected to {provider === 'github' ? 'GitHub' : 'GitLab'} and returned here after authorization.
          </span>
        </div>
      )}
    </div>
  );

  const renderScmOrgStep = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>Select Organisation / Group</h3>
      <p style={styles.stepDescription}>
        {availableOrgs.length > 0
          ? `Select the ${provider === 'github' ? 'GitHub organisation' : 'GitLab group'} you want to connect, or enter a name manually.`
          : `Enter the ${provider === 'github' ? 'GitHub organisation' : 'GitLab group'} name you want to connect.`}
      </p>

      {/* Loading state */}
      {loadingOrgs && (
        <div style={styles.orgLoadingContainer}>
          <span style={styles.orgLoadingText}>
            Loading available {provider === 'github' ? 'organisations' : 'groups'}…
          </span>
        </div>
      )}

      {/* Info message if orgs couldn't be loaded */}
      {orgsError && !loadingOrgs && (
        <div style={styles.orgInfoBanner}>
          {orgsError}
        </div>
      )}

      {/* Selectable org list */}
      {!loadingOrgs && availableOrgs.length > 0 && (
        <div style={styles.orgListContainer}>
          <label style={styles.label}>
            Available {provider === 'github' ? 'Organisations' : 'Groups'}
          </label>
          <div style={styles.orgList} role="listbox" aria-label="Available organisations">
            {availableOrgs.map((org) => (
              <button
                key={org.name}
                type="button"
                role="option"
                aria-selected={scmOrgName === org.name}
                onClick={() => { setScmOrgName(org.name); setError(''); setShowManualInput(false); }}
                style={{
                  ...styles.orgListItem,
                  borderColor: scmOrgName === org.name ? C.blue : C.border,
                  background: scmOrgName === org.name ? C.blueLt : C.white,
                }}
              >
                {org.avatar_url && (
                  <img
                    src={org.avatar_url}
                    alt=""
                    style={styles.orgAvatar}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div style={styles.orgItemText}>
                  <span style={styles.orgItemName}>{org.name}</span>
                  {org.description && (
                    <span style={styles.orgItemDesc}>
                      {org.description.length > 60
                        ? org.description.substring(0, 60) + '…'
                        : org.description}
                    </span>
                  )}
                </div>
                {scmOrgName === org.name && <span style={styles.orgCheckmark}>✓</span>}
              </button>
            ))}
          </div>

          {/* Toggle to manual input */}
          <button
            type="button"
            onClick={() => setShowManualInput(!showManualInput)}
            style={styles.manualToggleBtn}
          >
            {showManualInput ? 'Hide manual input' : 'Enter name manually instead'}
          </button>
        </div>
      )}

      {/* Manual text input (shown when no orgs available, or user toggles it) */}
      {!loadingOrgs && (showManualInput || availableOrgs.length === 0) && (
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="scm-org-name">
            {provider === 'github' ? 'Organisation Name' : 'Group Name'} <span style={styles.required}>*</span>
          </label>
          <input
            id="scm-org-name"
            type="text"
            value={scmOrgName}
            onChange={(e) => setScmOrgName(e.target.value)}
            placeholder={provider === 'github' ? 'e.g. my-github-org' : 'e.g. my-gitlab-group'}
            maxLength={128}
            style={styles.input}
            aria-required="true"
          />
          <span style={styles.hintText}>
            This should match the organisation or group name in your SCM provider.
          </span>
        </div>
      )}
    </div>
  );

  const renderConfirmStep = () => (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>Confirm Connection</h3>
      <p style={styles.stepDescription}>
        Review the details below and confirm to create the SCM connection.
      </p>

      {/* Success state */}
      {createSuccess && (
        <div style={styles.successBanner} role="status" aria-live="polite">
          <span style={styles.successIcon}>✓</span>
          <span>Connection created successfully!</span>
        </div>
      )}

      {/* Loading overlay on summary */}
      <div style={{
        ...styles.summaryCard,
        opacity: loading ? 0.6 : 1,
        pointerEvents: loading ? 'none' : 'auto',
      }}>
        <div style={styles.summaryRow}>
          <span style={styles.summaryLabel}>Provider</span>
          <span style={styles.summaryValue}>{provider === 'github' ? 'GitHub' : 'GitLab'}</span>
        </div>
        <div style={styles.summaryRow}>
          <span style={styles.summaryLabel}>Connection Name</span>
          <span style={styles.summaryValue}>{connectionName}</span>
        </div>
        <div style={styles.summaryRow}>
          <span style={styles.summaryLabel}>Auth Type</span>
          <span style={styles.summaryValue}>{authType === 'pat' ? 'Personal Access Token' : 'OAuth'}</span>
        </div>
        <div style={{ ...styles.summaryRow, borderBottom: 'none' }}>
          <span style={styles.summaryLabel}>{provider === 'github' ? 'Organisation' : 'Group'}</span>
          <span style={styles.summaryValue}>{scmOrgName}</span>
        </div>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div style={styles.loadingIndicator} role="status" aria-live="polite">
          <span style={styles.loadingText}>Creating connection…</span>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 0: return renderProviderStep();
      case 1: return renderCredentialsStep();
      case 2: return renderScmOrgStep();
      case 3: return renderConfirmStep();
      default: return null;
    }
  };

  // ── Footer buttons ──────────────────────────────────────────────────────────
  const renderFooter = () => {
    const isFirst = step === 0;
    const isLast = step === STEPS.length - 1;

    return (
      <div style={styles.footer}>
        <button
          type="button"
          onClick={onClose}
          style={styles.cancelBtn}
        >
          Cancel
        </button>
        <div style={styles.footerRight}>
          {!isFirst && (
            <button
              type="button"
              onClick={goBack}
              style={styles.backBtn}
              disabled={loading}
            >
              ← Back
            </button>
          )}
          {isLast ? (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || createSuccess}
              style={{
                ...styles.nextBtn,
                background: createSuccess ? C.green : C.green,
                opacity: (loading || createSuccess) ? 0.6 : 1,
                cursor: (loading || createSuccess) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Creating…' : createSuccess ? '✓ Created' : 'Create Connection'}
            </button>
          ) : (
            <button
              type="button"
              onClick={
                step === 0 ? handleProviderNext :
                step === 1 ? handleCredentialsNext :
                step === 2 ? handleScmOrgNext :
                goNext
              }
              disabled={validatingPat}
              style={{
                ...styles.nextBtn,
                opacity: validatingPat ? 0.6 : 1,
                cursor: validatingPat ? 'not-allowed' : 'pointer',
              }}
            >
              {validatingPat ? 'Validating…' : 'Next →'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────────────────────
  if (!open) return null;

  return (
    <div style={styles.overlay} role="presentation" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scm-wizard-title"
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <h2 id="scm-wizard-title" style={styles.title}>Connect SCM Provider</h2>
          <button
            type="button"
            onClick={onClose}
            style={styles.closeBtn}
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Error message */}
        {error && (
          <div style={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        {/* Step content */}
        {renderCurrentStep()}

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
  },
  dialog: {
    background: C.white,
    borderRadius: 12,
    width: '100%',
    maxWidth: 560,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 4px 16px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 0',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: C.text,
    margin: 0,
  },
  closeBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    fontSize: 16,
    color: C.slate,
    cursor: 'pointer',
    borderRadius: 6,
  },
  // Step indicator
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 24px 12px',
    gap: 0,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  stepCircle: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepLabel: {
    fontSize: 11,
    whiteSpace: 'nowrap',
  },
  stepLine: {
    width: 24,
    height: 2,
    background: C.border,
    margin: '0 4px',
    flexShrink: 0,
  },
  // Step content
  stepContent: {
    padding: '12px 24px 20px',
    flex: 1,
  },
  stepTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 4px',
  },
  stepDescription: {
    fontSize: 12,
    color: C.slate,
    margin: '0 0 16px',
    lineHeight: 1.5,
  },
  // Provider cards
  providerCards: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  providerCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '20px 16px',
    borderRadius: 10,
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'center',
  },
  providerIcon: {
    fontSize: 28,
    lineHeight: 1,
  },
  providerName: {
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
  },
  providerDesc: {
    fontSize: 11,
    color: C.slate,
  },
  // Auth type
  authTypeRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  authTypeBtn: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  // Form fields
  fieldGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.textMid,
    marginBottom: 4,
  },
  required: {
    color: C.red,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.white,
    boxSizing: 'border-box',
  },
  hintText: {
    display: 'block',
    fontSize: 11,
    color: C.slateLight,
    marginTop: 4,
  },
  // OAuth placeholder
  oauthPlaceholder: {
    padding: '16px',
    background: C.bg,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    textAlign: 'center',
  },
  oauthText: {
    fontSize: 12,
    color: C.textMid,
    margin: '0 0 12px',
  },
  oauthBtn: {
    padding: '10px 20px',
    borderRadius: 6,
    border: 'none',
    background: C.navy,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 8,
  },
  // Summary
  summaryCard: {
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '16px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${C.border}`,
  },
  summaryLabel: {
    fontSize: 12,
    color: C.slate,
    fontWeight: 500,
  },
  summaryValue: {
    fontSize: 13,
    color: C.text,
    fontWeight: 600,
  },
  // Error
  errorBanner: {
    margin: '0 24px',
    padding: '10px 14px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 500,
    border: `1px solid ${C.red}20`,
  },
  // Success
  successBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 14px',
    borderRadius: 6,
    background: C.greenLt,
    color: '#065F46',
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${C.green}30`,
    marginBottom: 16,
  },
  successIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: C.green,
    color: C.white,
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  // Loading indicator
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 0',
    marginTop: 12,
  },
  loadingText: {
    fontSize: 12,
    color: C.slate,
    fontStyle: 'italic',
  },
  // Footer
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderTop: `1px solid ${C.border}`,
  },
  footerRight: {
    display: 'flex',
    gap: 8,
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  backBtn: {
    padding: '8px 14px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  nextBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // Org list (Step 3)
  orgLoadingContainer: {
    padding: '24px 16px',
    textAlign: 'center',
    background: C.bg,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    marginBottom: 16,
  },
  orgLoadingText: {
    fontSize: 12,
    color: C.slate,
    fontStyle: 'italic',
  },
  orgInfoBanner: {
    padding: '10px 14px',
    borderRadius: 6,
    background: C.yellowLt,
    color: '#92400E',
    fontSize: 12,
    fontWeight: 500,
    border: `1px solid ${C.yellow}30`,
    marginBottom: 12,
  },
  orgListContainer: {
    marginBottom: 16,
  },
  orgList: {
    maxHeight: 200,
    overflowY: 'auto',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    marginTop: 4,
  },
  orgListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 12px',
    border: 'none',
    borderBottom: `1px solid ${C.border}`,
    background: C.white,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.1s',
    borderLeft: '3px solid transparent',
  },
  orgAvatar: {
    width: 24,
    height: 24,
    borderRadius: 4,
    flexShrink: 0,
  },
  orgItemText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  orgItemName: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  orgItemDesc: {
    fontSize: 11,
    color: C.slate,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  orgCheckmark: {
    fontSize: 14,
    color: C.blue,
    fontWeight: 700,
    flexShrink: 0,
  },
  manualToggleBtn: {
    marginTop: 8,
    padding: 0,
    border: 'none',
    background: 'none',
    color: C.blue,
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};
